import { WORDS } from '../constants/wordlist'
import { VALID_GUESSES } from '../constants/validGuesses'
import { WRONG_SPOT_MESSAGE, NOT_CONTAINED_MESSAGE } from '../constants/strings'
import { getGuessStatuses } from './statuses'
import { default as GraphemeSplitter } from 'grapheme-splitter'
import { ACCENTED_WORDS } from '../constants/accentedWords'

export const isWordInWordList = (word: string) => {
  return (
    WORDS.includes(localeAwareLowerCase(word)) || 
    VALID_GUESSES.includes(localeAwareLowerCase(word)) ||
    localeAwareLowerCase(word) in ACCENTED_WORDS
  )
}
export const isWordInAccentedList = (word: string) => {
  return localeAwareLowerCase(word) in ACCENTED_WORDS
}

export const getAccentedWord = (word: string) => {
  return ACCENTED_WORDS[localeAwareLowerCase(word)].toUpperCase()
}

export const isWinningWord = (word: string) => {
  return normalizeWord(solution) === word
}

export const normalizeWord = (word: string) => {
  return word.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

// build a set of previously revealed letters - present and correct
// guess must use correct letters in that space and any other revealed letters
// also check if all revealed instances of a letter are used (i.e. two C's)
export const findFirstUnusedReveal = (word: string, guesses: string[]) => {
  if (guesses.length === 0) {
    return false
  }

  const lettersLeftArray = new Array<string>()
  const guess = guesses[guesses.length - 1]
  const statuses = getGuessStatuses(solution, guess)
  const splitWord = unicodeSplit(word)
  const splitGuess = unicodeSplit(guess)

  for (let i = 0; i < splitGuess.length; i++) {
    if (statuses[i] === 'correct' || statuses[i] === 'present') {
      lettersLeftArray.push(splitGuess[i])
    }
    if (statuses[i] === 'correct' && splitWord[i] !== splitGuess[i]) {
      return WRONG_SPOT_MESSAGE(splitGuess[i], i + 1)
    }
  }

  // check for the first unused letter, taking duplicate letters
  // into account - see issue #198
  let n
  for (const letter of splitWord) {
    n = lettersLeftArray.indexOf(letter)
    if (n !== -1) {
      lettersLeftArray.splice(n, 1)
    }
  }

  if (lettersLeftArray.length > 0) {
    return NOT_CONTAINED_MESSAGE(lettersLeftArray[0])
  }
  return false
}

export const unicodeSplit = (word: string) => {
  return new GraphemeSplitter().splitGraphemes(word)
}

export const unicodeLength = (word: string) => {
  return unicodeSplit(word).length
}

export const localeAwareLowerCase = (text: string) => {
  return process.env.REACT_APP_LOCALE_STRING
    ? text.toLocaleLowerCase(process.env.REACT_APP_LOCALE_STRING)
    : text.toLowerCase()
}

export const localeAwareUpperCase = (text: string) => {
  return process.env.REACT_APP_LOCALE_STRING
    ? text.toLocaleUpperCase(process.env.REACT_APP_LOCALE_STRING)
    : text.toUpperCase()
}

export const getWordOfDay = () => {
  // January 1, 2022 Game Epoch
  const epoch = new Date(2022,4)
  const start = new Date(epoch)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  let index = 0
  while (start < today) {
    index++
    start.setDate(start.getDate() + 1)
  }

  const nextDay = new Date(today)
  nextDay.setDate(today.getDate() + 1)

  return {
    solution: localeAwareUpperCase(WORDS[index % WORDS.length]),
    solutionIndex: index,
    tomorrow: nextDay.valueOf(),
  }
}

export const { solution, solutionIndex, tomorrow } = getWordOfDay()

export const formatGrammaticalClass = (abbr: string) => {
  const stripped = abbr.replace('/n', ' ')

  if (
    ' s.f. '.includes(stripped) ||
    ' sf '.includes(stripped) ||
    ' f. '.includes(stripped)
  ) {
    return 'Substantivo Feminino'
  }

  if (' s.f.pl. '.includes(stripped) || ' s.f. pl. '.includes(stripped)) {
    return 'Substantivo Feminino Plural'
  }

  if (
    ' s.m. '.includes(stripped) ||
    ' sm '.includes(stripped) ||
    ' m. '.includes(stripped)
  ) {
    return 'Substantivo Masculino'
  }
  if (' s.m.pl. '.includes(stripped) || ' s.m. pl. '.includes(stripped)) {
    return 'Substantivo Masculino Plural'
  }
  if (' adj. '.includes(stripped) || ' adj '.includes(stripped)) {
    return 'Adjetivo'
  }
  if (' adj.f. '.includes(stripped)) {
    return 'Adjetivo Feminino'
  }
  if (' adj.m. '.includes(stripped)) {
    return 'Adjetivo Masculino'
  }
  if (' v. '.includes(stripped)) {
    return 'Verbo'
  }
  if (' v.t. '.includes(stripped)) {
    return 'Verbo Transitivo'
  }
  if (
    ' v.t.d '.includes(stripped) ||
    ' v.t.d. '.includes(stripped) ||
    ' vtd '.includes(stripped)
  ) {
    return 'Verbo Transitivo Direto'
  }
  if (
    ' v.t.i '.includes(stripped) ||
    ' v.t.i. '.includes(stripped) ||
    ' vti '.includes(stripped)
  ) {
    return 'Verbo Transitivo Indireto'
  }
  if (' v.i. '.includes(stripped)) {
    return 'Verbo Intransitivo'
  }
  if (' v.bit. '.includes(stripped)) {
    return 'Verbo Bitransitivo'
  }
  if (' v.pron. '.includes(stripped) || ' v.pr. '.includes(stripped)) {
    return 'Verbo Pronominal'
  }
  if (' v.reg.mult. '.includes(stripped)) {
    return 'Verbo de Regência Múltipla'
  }
  if (' v.pred. '.includes(stripped)) {
    return 'Verbo Predicativo'
  }
  if (' v. i. bot. '.includes(stripped)) {
    return 'Verbo Intransitivo Botânica'
  }
  if (' v. i. fam. '.includes(stripped)) {
    return 'Verbo Intransitivo Familiar'
  }
  if (' v. i. náut. '.includes(stripped)) {
    return 'Verbo Intransitivo Náutica'
  }
  if (' v. i. pop. '.includes(stripped)) {
    return 'Verbo Intransitivo Popular'
  }
  if (' adv. '.includes(stripped)) {
    return 'Advérbio'
  }
  if (' pron. '.includes(stripped)) {
    return 'Pronome'
  }
  if (' pr.dem. '.includes(stripped)) {
    return 'Pronome Demonstrativo'
  }
  if (' pr.excl. '.includes(stripped)) {
    return 'Pronome Exclamativo'
  }
  if (' pr.indef. '.includes(stripped)) {
    return 'Pronome Indefinido'
  }
  if (' pr.interr. '.includes(stripped)) {
    return 'Pronome Interrogativo'
  }
  if (' pr.pess. '.includes(stripped)) {
    return 'Pronome Pessoal'
  }
  if (' pr.poss. '.includes(stripped)) {
    return 'Pronome Possessivo'
  }
  if (' pr.rel. '.includes(stripped)) {
    return 'Pronome Relativo'
  }
  if (' loc. '.includes(stripped)) {
    return 'Locução'
  }
  if (' loc.subst. '.includes(stripped) || ' loc.sub. '.includes(stripped)) {
    return 'Locução Substantiva'
  }
  if (' loc.adv. '.includes(stripped)) {
    return 'Locução Adverbial'
  }
  if (' loc.a. '.includes(stripped) || ' loc.adj. '.includes(stripped)) {
    return 'Locução Adjetiva'
  }
  if (' loc.conj. '.includes(stripped)) {
    return 'Locução Conjuntiva'
  }
  if (' loc.interj. '.includes(stripped)) {
    return 'Locução Interjetiva'
  }
  if (' loc.prep. '.includes(stripped)) {
    return 'Locução Prepositiva'
  }
  if (' loc.verb. '.includes(stripped)) {
    return 'Locução Verbal'
  }
  if (' loc.lat. '.includes(stripped)) {
    return 'Locução Latina'
  }
  if (' interj. '.includes(stripped)) {
    return 'Interjeição'
  }
  if (' expr. '.includes(stripped)) {
    return 'Expressão'
  }
  if (' num. '.includes(stripped)) {
    return 'Numeral'
  }
  if (' pref. '.includes(stripped)) {
    return 'Prefixo'
  }
  if (' prep. '.includes(stripped)) {
    return 'Preposição'
  }
  if (' comb. '.includes(stripped)) {
    return 'Combinação'
  }
  if (' conj. '.includes(stripped)) {
    return 'Conjunção'
  }
  if (' contr. '.includes(stripped)) {
    return 'Contração'
  }
  if (' sigl. '.includes(stripped)) {
    return 'Sigla'
  }

  return stripped
}
